import React, { useCallback } from 'react';
import { faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { select } from 'd3';
import { cloneDeep, isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectControls } from '../../redux/controls';
import {
  selectItem, selectItemPerformanceData, selectItemPerformanceDataLoading,
  selectItemResponse, selectItemResponseLoading, selectItemSampleResponses, selectItemSampleResponsesLoading,
  selectItemScoreGuide, selectItemScoreGuideLoading
} from '../../redux/itemDetail';
import { IItemResponse, IPerformanceData, PerformanceDataTableResponse } from '../../redux/itemDetail/models';
import { selectItemRequest, selectTabDataEntities, selectTabItemCount } from '../../redux/queryResults';
import {
  printHandler, setAdditionalMaterials, toggleAdditionalMaterialAllOff, SVG_HEIGHT,
  SVG_WIDTH, toggleAdditionalMaterial,
  IItemDetailProps, renderChart, showPerformancePanel
} from './models';
import './ItemDetail.scss';
import Spinner from '../Spinner/Spinner';
import { selectPDVariables, selectPDVariablesSelectors } from '../../redux/explorePanel';
import { genDDMenuItemLabel } from '../QueryPanel/models';
import { tabBackToSelectedRow } from '../../Components/ShareLink/models/ShareLink.utils';
import ga4 from '../../shared/analyticsEvents';
import gaEvents from '../../shared/analyticsEvents';
import printerIcon from '../../images/printer.png';
import openTaskIcon from '../../images/open_task.png';

const staticMenuStyle = {
  width: '100%'
};
export const ItemDetail = ({ ItemIndex, SetItemDetailHandler }: IItemDetailProps): JSX.Element => {
  const svgRef = useRef<SVGSVGElement>(null);
  const questionTabBtn = useRef<HTMLButtonElement>(null);
  const sampleTabBtn = useRef<HTMLButtonElement>(null);
  const performanceTabBtn = useRef<HTMLButtonElement>(null);
  const mathPRTabBtn = useRef<HTMLButtonElement>(null);
  const resizeDiv = useRef<HTMLDivElement>(null);

  const controls = useSelector(selectControls);
  const itemCount = useSelector(selectTabItemCount);
  const item = useSelector(selectItem);
  const itemRequest = useSelector(selectItemRequest);
  const itemResponse = useSelector(selectItemResponse);

  const itemResponseLoading = useSelector(selectItemResponseLoading);
  const itemScoringGuide = useSelector(selectItemScoreGuide);
  const itemScoringGuideLoading = useSelector(selectItemScoreGuideLoading);
  const itemSampleResponses = useSelector(selectItemSampleResponses);
  const itemSampleResponsesLoading = useSelector(selectItemSampleResponsesLoading);
  const itemPerformanceData = useSelector(selectItemPerformanceData);
  const itemPerformanceDataLoading = useSelector(selectItemPerformanceDataLoading);


  // For Showcasing PR Math
  const gridItemEntities = useSelector(selectTabDataEntities);

  const pdVariablesSelector = useSelector(selectPDVariablesSelectors);
  const pdVariables = useSelector(selectPDVariables);

  const [response, setResponse] = useState<IItemResponse | null>(null);

  const [addMaterialNodes, setAddMaterialNodes] = useState<NodeListOf<Element> | null>(null);
  const [addMaterialNodesButtons, setAddMaterialNodesButtons] = useState<JSX.Element[]>([]);
  const [scoreGuide, setScoreGuide] = useState<string>('');
  const [sampleResponses, setSampleResponses] = useState<string>('');
  const [performanceData, setPerformanceData] = useState<PerformanceDataTableResponse[] | IPerformanceData | null>(null);
  const [hasData, setHasData] = useState(false);
  const [selectedPDVariables, setSelectedPDVariables] = useState<string[]>([]);
  const [selectedPDVariableIndex, setSelectedPDVariableIndex] = useState<number>(0);
  const [openPerfDataBlock, setOpenPerfDataBlock] = useState<boolean>(false);
  const [bookmarkedActiveTab, setBookmarkedActiveTab] = useState<string>('question');
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const LIST_BODY_MIN_HEIGHT = 30;

  const [manuallyTriggerApply] = useState<boolean>(false);

  const openTaskWindow = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  useEffect(() => {
    if (itemResponse) {
      const clonedResponse = cloneDeep(itemResponse);
      const itemHTML = document.createElement('div');
      itemHTML.innerHTML = clonedResponse.itemHTML;
      const additionalMat = itemHTML.querySelectorAll('.stimulus');
      const additionalMatLabel = itemHTML.querySelectorAll('.stimlabel');
      if (additionalMat.length > 0) {
        setAddMaterialNodes(additionalMat);
        setAddMaterialNodesButtons(setAdditionalMaterials(additionalMatLabel, toggleAdditionalMaterial, controls?.ShowAllItems?.controlText));
        additionalMat.forEach(el => {
          el.className += ' d-none';
          const stimLabel = el.firstChild as Element;
          if (stimLabel && stimLabel.className.includes('stimlabel')) {
            el.removeChild(stimLabel);
          }
        });
      } else {
        setAddMaterialNodesButtons([]);
        setAddMaterialNodes(null);
      }
      clonedResponse.itemHTML = itemHTML.outerHTML;
      setResponse(clonedResponse);
    } else {
      setResponse(null);
    }
  }, [itemResponse, controls]);

  useEffect(() => {
    itemScoringGuide ?
      setScoreGuide(itemScoringGuide) :
      setScoreGuide('');
  }, [itemScoringGuide]);

  useEffect(() => {
    itemSampleResponses ?
      setSampleResponses(itemSampleResponses) :
      setSampleResponses('');
  }, [itemSampleResponses]);

  useEffect(() => {
    if (itemPerformanceData) {
      const clonedPD = cloneDeep(itemPerformanceData);
      if (!Array.isArray(clonedPD)) {
        clonedPD.footnotes = clonedPD.footnotes.reverse();
        setHasData(clonedPD.chartHasData);
      } else {
        setHasData(!!clonedPD.length);
      }
      setPerformanceData(clonedPD);
    } else {
      setPerformanceData(null);
      setHasData(false);
    }
  }, [itemPerformanceData]);

  useEffect(() => {
    if (performanceData && 'chartHasData' in performanceData && svgRef.current) {
      const svg = select(svgRef.current);
      if (svgRef.current?.children.length !== 0) {
        svg.html('');
      }
      renderChart(
        svg,
        SVG_WIDTH,
        SVG_HEIGHT,
        performanceData.xAxisTitle,
        performanceData.series[0].dataPoints
      );
    }
  }, [performanceData]);


  const selectTabFromBookmarkQuery = useCallback((activeTab) => {

    const questionTabBtn = document.getElementById('pills-question-tab');
    const scoreTabBtn = document.getElementById('pills-scoring-tab');
    const sampleTabBtn = document.getElementById('pills-sample-tab');
    const performanceTabBtn = document.getElementById('pills-performance-tab');
    const mathematicsTabBtn = document.getElementById('pills-mathematics-tab');


    switch(activeTab.toUpperCase()) {
    case '0':
    case'QUESTION':
      setBookmarkedActiveTab('question');
      if (questionTabBtn && !manuallyTriggerApply) {
        questionTabBtn.click();
      }
      break;
    case '1':
    case 'SCORE':
      setBookmarkedActiveTab('score');
      if (scoreTabBtn && !manuallyTriggerApply) {
        scoreTabBtn.click();
      }
      break;
    case '2':
    case 'SAMPLE':
      setBookmarkedActiveTab('sample');
      if (!manuallyTriggerApply && sampleTabBtn) {
        sampleTabBtn.click();
      }
      break;
    case '3':
    case 'PERFORMANCE':
      setBookmarkedActiveTab('performance');
      if (performanceTabBtn && !manuallyTriggerApply) {
        performanceTabBtn.click();
        setOpenPerfDataBlock(true);
      }
      break;
    case '4':
    case 'PUERTORICO':
      if(itemRequest?.SubjectCode === 'MAT' && item?.hasSpanish){
        setBookmarkedActiveTab('PR');
        mathematicsTabBtn?.click();
      } else {
        setBookmarkedActiveTab('question');
        if (questionTabBtn && !manuallyTriggerApply) {
          questionTabBtn.click();
        }
      }
      break;
    default:
      setBookmarkedActiveTab('question');
    }
  },[itemRequest?.SubjectCode, manuallyTriggerApply, item?.hasSpanish]);

  useEffect(() => {
    if (itemRequest?.BookmarkedTab) {
      const activeTab = String(itemRequest?.BookmarkedTab);
      selectTabFromBookmarkQuery(activeTab);
    }
    const url = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, url);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemRequest?.BookmarkedTab] );


  // Fires when new item detail doesn't have active tab content
  useEffect(() => {
    if (item) {
      if (
        (sampleTabBtn.current?.classList.contains('active') && item.type === 'MC') ||
        (mathPRTabBtn.current?.classList.contains('active') && !item.hasSpanish)
      ) {
        questionTabBtn.current?.click();
        questionTabBtn.current?.focus();
      }
    }

  }, [item, hasData]);

  useEffect(() => {
    if (!!pdVariablesSelector && !isEmpty(pdVariablesSelector) && !!pdVariables) {
      const variablesList = pdVariables.split(',');
      setSelectedPDVariables(
        variablesList.map(vr => pdVariablesSelector[vr] as string)
      );
    } else {
      setSelectedPDVariables([]);
    }
    setSelectedPDVariableIndex(0);
  }, [pdVariables, pdVariablesSelector]);

  useEffect(() => {
    if (isPrinting) {
      ga4.onGA4ItemDetailNavigation('Print');
      printHandler();
      setIsPrinting(false);
    }
  }, [isPrinting]);

  useEffect(() => {
    toggleAdditionalMaterialAllOff();
  }, [addMaterialNodesButtons]);

  useEffect(() => {
    if (openPerfDataBlock) {
      showPerformancePanel();
      setOpenPerfDataBlock(false);
    }
  }, [openPerfDataBlock]);

  const resizeMouseDownHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    gaEvents.onGA4ResizeQuestionList();
    const mouseOrigPosition = e.clientY;
    const listBody = document.querySelector('.nqt-qr .tab-data-body') as HTMLDivElement;
    const listBodyHeight = Number.parseInt(listBody.style.maxHeight, 10);
    const fullTableElement = document.querySelector('table.table.table-body') as HTMLTableElement;
    const fullTableHeight = fullTableElement.offsetHeight;

    const resizeMouseMoveHandler = (e: MouseEvent) => {
      e.preventDefault();
      const listBody = document.querySelector('.nqt-qr .tab-data-body') as HTMLDivElement;
      const listBodyNewHeight = listBodyHeight + (e.clientY - mouseOrigPosition);
      if (listBodyNewHeight >= LIST_BODY_MIN_HEIGHT && listBodyNewHeight <= fullTableHeight) {
        listBody.style.maxHeight = `${listBodyNewHeight}px`;
      }
    };

    const resizeMouseUpHandler = (e: MouseEvent) => {
      e.preventDefault();
      window.removeEventListener('mousemove', resizeMouseMoveHandler);
      window.removeEventListener('mouseup', resizeMouseUpHandler);
    };

    window.addEventListener('mousemove', resizeMouseMoveHandler);
    window.addEventListener('mouseup', resizeMouseUpHandler);

  };

  return (
    <>
      <div ref={resizeDiv} onMouseDown={resizeMouseDownHandler} className="before-element-resize"></div>
      <div className="item-detail">
        <div className="row row-cols-3 top-row">
          <div className="item-detail-navbar col-7">
            <ul className="nav nav-pills" id="pills-tab" role="tablist">
              <li className="nav-item" role="presentation">
                <button className={`nav-link ${bookmarkedActiveTab === 'question' ? ' active' : ''}`}
                  onMouseDown={(event) => { ga4.onGA4ItemDetailNavigation('Question', event); }}
                  ref={questionTabBtn} id="pills-question-tab" data-bs-toggle="pill"
                  data-bs-target="#pills-question" type="button" role="tab"
                  aria-controls="pills-question" aria-selected="true"
                  onKeyDown={tabBackToSelectedRow}
                  dangerouslySetInnerHTML={{ __html: controls?.SelQ?.controlText || '' }}>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button className={`nav-link ${bookmarkedActiveTab === 'score' ? ' active' : ''}`} id="pills-scoring-tab" data-bs-toggle="pill"
                  onMouseDown={(event) => { ga4.onGA4ItemDetailNavigation('Answer Key', event); }}
                  data-bs-target="#pills-scoring" type="button" role="tab"
                  aria-controls="pills-scoring" aria-selected="false"
                  dangerouslySetInnerHTML={{ __html: controls?.SelKey?.controlText || '' }}>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button className={`nav-link ${bookmarkedActiveTab === 'sample' ? ' active' : ''}`}
                  ref={sampleTabBtn}
                  onMouseDown={(event) => { ga4.onGA4ItemDetailNavigation('Student Responses', event); }}
                  id="pills-sample-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-sample"
                  type="button"
                  role="tab"
                  aria-controls="pills-sample"
                  aria-selected="false"
                  dangerouslySetInnerHTML={{ __html: controls?.SelSR?.controlText || '' }}>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button className={`nav-link ${bookmarkedActiveTab === 'performance' ? ' active' : ''}`}
                  ref={performanceTabBtn}
                  id="pills-performance-tab"
                  onMouseDown={(event) => { ga4.onGA4ItemDetailNavigation('Student Performance', event); setOpenPerfDataBlock(true); }}
                  data-bs-toggle="pill"
                  data-bs-target="#pills-performance"
                  type="button"
                  role="tab"
                  aria-controls="pills-performance"
                  aria-selected="false"
                  dangerouslySetInnerHTML={{ __html: controls?.SelPer?.controlText || '' }}>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                {
                  item && (item.enaep === '1' || item.enaep == '2' || item.enaep == '3') ?
                    <a className={`nav-link ${bookmarkedActiveTab === 'presentation' ? ' active' : ''}`} href={item.enaepUrl} target="_blank" rel="noopener noreferrer"
                      dangerouslySetInnerHTML={{ __html: controls?.SelENAEP?.controlText || '' }}
                    >
                    </a> :
                    <button className={`nav-link ${bookmarkedActiveTab === 'presentation' ? ' active' : ''}`} disabled={true} type="button" role="tab"
                      dangerouslySetInnerHTML={{ __html: controls?.SelENAEP?.controlText || '' }}>
                    </button>
                }
              </li>
              {
                (
                  itemRequest?.SubjectCode === 'MAT' &&
                  gridItemEntities &&
                  Object.values(gridItemEntities).some(gi => gi.hasSpanish)) &&
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${bookmarkedActiveTab === 'PR' ? ' active' : ''}`}
                    disabled={!!item && !item.hasSpanish}
                    ref={mathPRTabBtn}
                    onMouseDown={(event) => { ga4.onGA4ItemDetailNavigation('Puerto Rico', event); }}
                    id="pills-mathematics-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-mathematics"
                    type="button"
                    role="tab"
                    aria-controls="pills-mathematics"
                    aria-selected="false"
                    dangerouslySetInnerHTML={{ __html: controls?.SelMathPR?.controlText || '' }}>
                  </button>
                </li>
              }
            </ul>
          </div>
          <div className="item-detail-buttons col-5">
            <div className='container'>
            </div>
            <div className="item-detail-range ">
              <button className='item-detail-range--button'
                disabled={ItemIndex === 1}
                onClick={() => SetItemDetailHandler(ItemIndex - 2)}>
                <span className='visually-hidden text-dark'>{controls?.AriaPrev?.controlText}</span>
                <FontAwesomeIcon icon={faCaretLeft} />
              </button>
              <span className='text-nowrap'>
                {controls?.QNum?.controlText.replace(/\$X\$/, ItemIndex.toString()).replace(/\$Y\$/, itemCount ? itemCount.toString() : 'Err')}
              </span>
              <button className='item-detail-range--button'
                disabled={!!itemCount && itemCount === ItemIndex}
                onClick={() => SetItemDetailHandler(ItemIndex)}>
                <span className='visually-hidden text-dark'>{controls?.AriaNext?.controlText}</span>
                <FontAwesomeIcon icon={faCaretRight} />
              </button>
            </div>
          </div>
        </div>
        <div className="row" id="print-anchor">
          <div className="tab-content section-to-print" id="pills-tabContent">
            <div className="tab-pane fade show active" id="pills-question" role="tabpanel" aria-labelledby="pills-question-tab">
              {itemResponseLoading && <Spinner />}
              <section aria-label={controls?.QContent?.controlText} className="question-content container-fluid">
                {
                  addMaterialNodes && addMaterialNodes.length > 0 ?
                    <><div className="question-content--row-1 row row-cols-2">
                      <div className="question-content--additional-materials col-12">
                        <div className="col-2"><div className='additional-materials--label'>{controls?.AddlMaterials?.controlText}</div></div>
                        <div className="col-7">
                          {
                            addMaterialNodesButtons
                          }</div>
                        <div className="col-3">
                          <div className="wrapper item-detail-buttons">
                            <div className="item-detail-print">
                              <img alt="Open Task" src={openTaskIcon} />
                              <button className="item-detail-wrapper-button"
                                disabled={
                                  !!itemResponseLoading ||
                                  !!itemScoringGuideLoading ||
                                  !!itemSampleResponsesLoading ||
                                  !!itemPerformanceDataLoading ||
                                  !item?.taskUrl
                                }
                                onClick={() => { !!item?.taskUrl && openTaskWindow(item.taskUrl); }}
                                dangerouslySetInnerHTML={{ __html: 'Open Task' }}>
                              </button>
                            </div>
                            <div className="item-detail-print">
                              <img alt="Print Question Detail" src={printerIcon} />
                              <button className="item-detail-wrapper-button"
                                disabled={
                                  !!itemResponseLoading ||
                                  !!itemScoringGuideLoading ||
                                  !!itemSampleResponsesLoading ||
                                  !!itemPerformanceDataLoading
                                }
                                onClick={() => { setIsPrinting(true); }}
                                dangerouslySetInnerHTML={{ __html: 'Print Question Detail' }}>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <h3 className='question-content--title-line-1 extended col-6' >{response?.titleLine1}</h3>
                      <h3 className="question-content-title-line-2 col-12">{response?.titleLine2}</h3>
                      <span className="question-content--question-id col-3">{response?.questionID}</span>
                    </div>
                    <div className="question-content--html-container" dangerouslySetInnerHTML={{ __html: response ? response.itemHTML : '' }}></div>
                    </> :
                    <>
                      <div className="question-content--row-1 row row-cols-2">

                        <div className="col-12 offset-9 question-content--task-print-buttons">
                          { !itemResponseLoading ?
                            <div className="wrapper item-detail-buttons">
                              <div className="item-detail-print">
                                <img alt="Open Task" src={openTaskIcon} />
                                <button className="item-detail-wrapper-button"
                                  disabled={
                                    !!itemResponseLoading ||
                                      !!itemScoringGuideLoading ||
                                      !!itemSampleResponsesLoading ||
                                      !!itemPerformanceDataLoading ||
                                      !item?.taskUrl
                                  }
                                  onClick={() => { !!item?.taskUrl && openTaskWindow(item.taskUrl); }}
                                  dangerouslySetInnerHTML={{ __html: 'Open Task' }}>
                                </button>
                              </div>
                              <div className="item-detail-print">
                                <img alt="Print Question Detail" src={printerIcon} />
                                <button className="item-detail-wrapper-button"
                                  disabled={
                                    !!itemResponseLoading ||
                                      !!itemScoringGuideLoading ||
                                      !!itemSampleResponsesLoading ||
                                      !!itemPerformanceDataLoading
                                  }
                                  onClick={() => { setIsPrinting(true); }}
                                  dangerouslySetInnerHTML={{ __html: 'Print Question Detail' }}>
                                </button>
                              </div>
                            </div> :
                            null
                          }
                        </div>
                        <h3 className='question-content--title-line-1 col-6' >{response?.titleLine1}</h3>
                        <h3 className="question-content-title-line-2 col-9">{response?.titleLine2}</h3>
                        <span className='question-content--question-id col-6'>{response?.questionID}</span>

                      </div>
                      <div className="question-content--html-container col-9" dangerouslySetInnerHTML={{ __html: response ? response.itemHTML : '' }}></div>
                    </>
                }
              </section>
            </div>
            <div className="tab-pane fade" id="pills-scoring" role="tabpanel" aria-labelledby="pills-scoring-tab">
              {itemScoringGuideLoading && <Spinner />}
              <section className="scoring-content container-fluid">
                <div className="scoring-content--html-container" dangerouslySetInnerHTML={{ __html: scoreGuide }}></div>
              </section>
            </div>
            <div className="tab-pane fade" id="pills-sample" role="tabpanel" aria-labelledby="pills-sample-tab">
              {itemSampleResponsesLoading && <Spinner />}
              <section className='sample-content container-fluid'>
                <div className="sample-content--html-container" dangerouslySetInnerHTML={{ __html: sampleResponses }}></div>
              </section>
            </div>
            <div className="tab-pane fade" id="pills-performance" role="tabpanel" aria-labelledby="pills-performance-tab">
              {itemPerformanceDataLoading && <Spinner containerClassName='item-performance-data' />}
              <section className={
                'performance-content container-fluid'
              }>
                {
                  hasData ?
                    performanceData && !Array.isArray(performanceData) ?
                      <div className="row row-cols-1">
                        {performanceData.titles.map((title: string, ind: number) => (
                          <div key={'chart-title-' + ind}
                            dangerouslySetInnerHTML={{ __html: title }}
                            className="performance--title col">
                          </div>
                        ))}
                        <div className="performance--svg-container col">
                          <svg className="svg-chart"
                            ref={svgRef}
                            height={SVG_HEIGHT}
                            width={SVG_WIDTH}>
                          </svg>
                        </div>
                        {performanceData.footnotes.map((footnote: string, ind: number) => (
                          <div key={'chart-footnotes-' + ind} className="performance--footer col">{footnote}</div>
                        ))}
                      </div> :
                      <div className="performance--tables row row-cols-1">
                        {
                          (selectedPDVariables.length > 0 && selectedPDVariableIndex >= 0 && selectedPDVariableIndex < selectedPDVariables.length && !isPrinting) &&
                          <div className={`col${(selectedPDVariables.length === 1) ? ' d-none' : ''}`}>
                            <div className="dropdown variable-selectors">
                              <button className="nqt-dropdown-btn btn dropdown-toggle"
                                type="button"
                                id="dropdownPDVariableMenuButton"
                                data-bs-toggle="dropdown"
                                aria-expanded="false">
                                <span className='dropdown-interior'>
                                  {
                                    selectedPDVariables[selectedPDVariableIndex]
                                  }
                                </span>
                                <span className='dropdown-arrow'></span>
                              </button>
                              <ul className="dropdown-menu" style={staticMenuStyle} aria-labelledby="dropdownPDVariableMenuButton">
                                {genDDMenuItemLabel(controls?.XplrVars?.controlText)}
                                {
                                  selectedPDVariables.map((vr, ind) => (
                                    <li key={`id-vr-${ind}`}>
                                      <a className={'dropdown-item' + (ind === selectedPDVariableIndex ? ' selected' : '')}
                                        onClick={(e) => { e.preventDefault(); setSelectedPDVariableIndex(ind); }}
                                        href="#">
                                        {vr}
                                      </a>
                                    </li>
                                  ))
                                }
                              </ul>
                            </div>
                          </div>
                        }
                        <div className={`col pd-table--container${isPrinting ? ' printing' : ''}`} >
                          {
                            performanceData && performanceData.map((pd, tableIndex) => {
                              return (
                                <React.Fragment key={'pd-table-' + tableIndex}>
                                  {
                                    (isPrinting && tableIndex !== 0) &&
                                    <div className="pd-table--table-separator"></div>
                                  }
                                  <table tabIndex={tableIndex === selectedPDVariableIndex ? 0 : -1} className={`pd-table${(!isPrinting && tableIndex !== selectedPDVariableIndex) ? ' d-none' : ''}`} >
                                    <caption className={`pd-table--caption${(isPrinting && tableIndex !== 0) ? ' not-first' : ''}${pd.hasData ? '' : ' no-data'}`}>
                                      {
                                        pd.title.map((tItem, ind) => (
                                          <span key={'pd-table--caption-' + tableIndex + '-' + ind}
                                            dangerouslySetInnerHTML={{ __html: tItem }}>
                                          </span>
                                        ))
                                      }
                                    </caption>
                                    <colgroup>
                                      {
                                        pd.columnHeadingsList.map((item, ind) => (
                                          item.row === 1 && <col key={'pd-table--colgroup-' + tableIndex + '-' + ind} />
                                        ))
                                      }
                                    </colgroup>
                                    <thead>
                                      <tr className="pd-table--first-row-header">
                                        {
                                          pd.columnHeadingsList.map((item, ind) => {
                                            if (item.row === 1) {
                                              if (item.headingText) {
                                                return (
                                                  <th key={'first-row-header-' + tableIndex + '-' + ind}
                                                    className="pd-table--header-item"
                                                    scope='col'
                                                    colSpan={item.colSpan}
                                                    rowSpan={item.rowSpan}>
                                                    {item.headingText}
                                                  </th>
                                                );
                                              } else {
                                                return (
                                                  <td key={'first-row-header-' + tableIndex + '-' + ind}
                                                    className="pd-table--header-item no-data-item"
                                                    scope='col'
                                                    colSpan={item.colSpan}
                                                    rowSpan={item.rowSpan}>
                                                    {item.headingText}
                                                  </td>
                                                );
                                              }
                                            }
                                          })
                                        }
                                      </tr>
                                      <tr className="pd-table--second-row-header">
                                        {
                                          pd.columnHeadingsList.map((item, ind) => {
                                            if (item.row === 2) {
                                              if (item.headingText) {
                                                return (
                                                  <th key={'second-row-header-' + tableIndex + '-' + ind}
                                                    className="pd-table--header-item"
                                                    scope='col'
                                                    colSpan={item.colSpan}
                                                    rowSpan={item.rowSpan}>
                                                    {item.headingText}
                                                  </th>
                                                );
                                              } else {
                                                return (
                                                  <td key={'second-row-header-' + tableIndex + '-' + ind}
                                                    className="pd-table--header-item no-data-item"
                                                    scope='col'
                                                    colSpan={item.colSpan}
                                                    rowSpan={item.rowSpan}>
                                                    {item.headingText}
                                                  </td>
                                                );
                                              }
                                            }
                                          })
                                        }
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {
                                        Object.keys(pd.tabularDataList).map((key, index) => (
                                          <tr key={'data-row-' + tableIndex + '-' + index} >
                                            {
                                              Object.keys(pd.tabularDataList[key].tabDataRow).map((ky, ind) => (
                                                <td key={'data-item-' + tableIndex + '-' + ind} className="pd-table--data-item">
                                                  {
                                                    pd.tabularDataList[key].tabDataRow[ky].value
                                                  }
                                                </td>
                                              ))
                                            }
                                          </tr>
                                        ))
                                      }
                                    </tbody>
                                  </table>
                                  {
                                    pd.hasData &&
                                    <div className={`pd-table--footer col${(!isPrinting && tableIndex !== selectedPDVariableIndex) ? ' d-none' : ''}`}>
                                      {Object.keys(pd.footnotes).map((fnKey: string, ind: number) => (
                                        <div key={'table-footnotes-' + ind} className="pd-table--footnote">{pd.footnotes[fnKey]}</div>
                                      ))}
                                    </div>
                                  }
                                </React.Fragment>
                              );
                            })
                          }
                        </div>
                      </div>
                    :
                    <div className="no_response">{controls?.NoPerformanceData?.controlText}</div>
                }
              </section>
            </div>

            {
              item?.hasSpanish &&
              <div className="tab-pane fade" id="pills-mathematics" role="tabpanel" aria-labelledby="pills-mathematics-tab">
                <section className="mathematics-content container-fluid"
                  dangerouslySetInnerHTML={{ __html: response ? response.spanishItemHTML : '' }}>
                </section>
              </div>
            }
          </div>
        </div>

      </div>

    </>
  );
};

export default ItemDetail;